import Vue from 'vue'
import axios from 'axios'
import { auth, notify } from 'utils'
import { forIn, each } from 'lodash'
import store from 'vx/store'

let domainSplit = window.location.hostname.split('.')
let tenant = domainSplit[0]
let baseUrl = tenant
if (domainSplit.length >= 2) {
  baseUrl = process.env.API_URL.replace('{tenant}.', tenant + '.')
} else {
  baseUrl = process.env.API_URL.replace('{tenant}.', '')
}

axios.defaults.baseURL = baseUrl
axios.defaults.withCredentials = true

/**
 * Create JWT request interceptor so we are always sending a JWT with outgoing
 * requests to the API.
 */
axios.interceptors.request.use((config) => {
  config.headers.Region = Vue.prototype.$user && Vue.prototype.$user.region && Vue.prototype.$user.region.shortname ? Vue.prototype.$user.region.shortname : 'au'
  return config
})
axios.interceptors.response.use((response) => {
  return response
}, (error) => {
  // If we get an error response we first want to see if it is a 401. If it is, we'll check the config
  // to see how we should handle it. If "disableAutomaticReinitialization" is there then we want to
  // simply throw the error back out as we handle it in the "attemptViaToken" method. Otherwise
  // we'll attempt to re-initialize the session.
  if (error.response && error.response.status === 401) {
    if (error.config.disableAutomaticReinitialization) {
      return Promise.reject(error)
    }

    return auth.attemptSessionInitialization()
      .then(() => {
        // If our attempt to reinitialize the session worked we'll fire off the original request again
        // with the updated token. This should be a smooth process for the end user if a token
        // somehow expires.
        error.config.headers['Authorization'] = `Bearer ${auth.getToken()}`

        return axios.request(error.config)
      })
      .catch(() => {
        auth.redirectOnTokenExpired()
      })
  }

  // For 403s we'll throw a blanket error notification and redirect them back to the dashboard.
  if (error.response && error.response.status === 403) {
    if (error.response.data && error.response.config.url !== 'broadcasting/auth') {
      notify.error('You do not have permission to do that.')
      auth.redirectOnForbidden()
      return Promise.reject(new Error('Forbidden'))
    }
  }

  if (error.response && (error.response.status === 400 || error.response.status === 404 || error.response.status === 422 || error.response.status === 500)) {
    return Promise.reject(error)
  }
})

// Set Axios on our Vue instances so we can have easier access to it via "this.$http" should we
// need to make requests within our components.
Vue.prototype.$http = axios

export default axios

/**
 * Generate a URL to the API with a provided path.
 *
 * @param string path
 *
 * @return string
 */
export const apiUrl = (path) => {
  if (path === undefined) {
    return process.env.API_URL.replace('{tenant}', this.$tenantName)
  }

  return `${process.env.API_URL}/${path}`.replace('{tenant}', this.$tenantName)
}

/**
 * Generate a URL to the public website with a provided path.
 *
 *
 * @return string
 * @param path
 * @param region
 */
export const publicUrl = (path, region = null) => {
  let regionShortname = region
  if (!regionShortname) {
    regionShortname = this && this.$user ? this.$user.region.shortname : 'au'
  }
  if (this && this.$vlastMode) {
    if (path === undefined) {
      return `${process.env.PUBLIC_URL}`
    }

    return `${process.env.PUBLIC_URL}/${path}`
  }
  if (path === undefined) {
    return process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL.replace('{tenant}', window.location.hostname.split('.')[0])}/${regionShortname}` : null
  }

  return process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL.replace('{tenant}', window.location.hostname.split('.')[0])}/${regionShortname}/${path}` : null
}

/**
 * Generate a URL to the storage assets with a provided path.
 *
 * @param path
 * @returns
 */
export const apiAssetUrl = (path) => {
  return process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL.replace('{tenant}', window.location.hostname.split('.')[0])}/${path}` : null
}

/**
 * Generate a URL to the cdn delivered assets with a provided path.
 *
 * @param string path
 *
 * @return string
 */
export const assetUrl = (path) => {
  if (path === undefined) {
    return store.state.clientConfig.client_config.keys.asset_url
  }

  return `${store.state.clientConfig.client_config.keys.asset_url}/${path}`
}

/**
 * Handle server validation errors by adding them to an error bag.
 *
 * @return void
 */
export const handleServerErrors = (exception, errorBag, scope) => {
  if (exception.response && exception.response.status === 422) {
    forIn(exception.response.data.errors, (errors, key) => {
      each(errors, error => (errorBag.add(key, error, null, scope)))
    })
  }
}
